import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type Timestamp,
} from 'firebase/firestore'
import { DateTime } from 'luxon'
import { db } from 'config/firebase'
import type { AssistantChatMessage } from './useAssistantChat'

/** Private assistant threads. Not an assistant search collection. */
export const ASSISTANT_CHATS_COLLECTION = 'assistantChats'

const ACTIVE_CHAT_KEY = 'otw.assistant.activeChatId'

export type AssistantChatSummary = {
  id: string
  title: string
  updatedAt: number | null
}

export type AssistantChatMeta = {
  ownerId: string
  assistantId: string | null
}

const TRANSCRIPT_CAP = 200

/** Keep both sides of a shared thread. A longer copy of the same id wins. */
export function mergeTranscriptMessages(
  current: AssistantChatMessage[],
  incoming: AssistantChatMessage[],
): AssistantChatMessage[] {
  const byId = new Map<string, AssistantChatMessage>()
  for (const message of current) byId.set(message.id, message)
  for (const message of incoming) {
    const prev = byId.get(message.id)
    if (!prev) {
      byId.set(message.id, message)
      continue
    }
    byId.set(
      message.id,
      message.content.length >= prev.content.length ? { ...prev, ...message } : prev,
    )
  }
  const seen = new Set<string>()
  const ordered: AssistantChatMessage[] = []
  for (const message of current) {
    const next = byId.get(message.id)
    if (next) ordered.push(next)
    seen.add(message.id)
  }
  for (const message of incoming) {
    if (seen.has(message.id)) continue
    const next = byId.get(message.id)
    if (next) ordered.push(next)
    seen.add(message.id)
  }
  return ordered.slice(-TRANSCRIPT_CAP)
}

export function sortChatsByRecent(chats: AssistantChatSummary[]): AssistantChatSummary[] {
  return [...chats].sort((a, b) => {
    const delta = (b.updatedAt ?? 0) - (a.updatedAt ?? 0)
    if (delta !== 0) return delta
    return a.id.localeCompare(b.id)
  })
}

/** Compact stamp for the history row. Empty when the chat has no time yet. */
export function formatAssistantChatTime(updatedAt: number | null, now = Date.now()): string {
  if (!updatedAt) return ''
  const value = DateTime.fromMillis(updatedAt)
  if (!value.isValid) return ''
  const today = DateTime.fromMillis(now)
  if (value.hasSame(today, 'day')) return value.toFormat('h:mm a')
  if (value.hasSame(today, 'year')) return value.toFormat('LLL d, h:mm a')
  return value.toFormat('LLL d, yyyy')
}

/** Collapse whitespace and cap at the Firestore title limit. Null when empty. */
export function normalizeAssistantChatTitle(content: string): string | null {
  const line = content.replace(/\s+/g, ' ').trim()
  if (!line) return null
  return line.length > 80 ? `${line.slice(0, 79)}…` : line
}

export function chatTitleFromText(content: string): string {
  return normalizeAssistantChatTitle(content) ?? 'New chat'
}

export function readActiveChatId(): string | null {
  try {
    return sessionStorage.getItem(ACTIVE_CHAT_KEY)
  } catch {
    return null
  }
}

export function writeActiveChatId(id: string | null) {
  try {
    if (id) sessionStorage.setItem(ACTIVE_CHAT_KEY, id)
    else sessionStorage.removeItem(ACTIVE_CHAT_KEY)
  } catch {
    // ignore quota / private mode
  }
}

function asMessages(value: unknown): AssistantChatMessage[] {
  if (!Array.isArray(value)) return []
  return value.filter(
    (message): message is AssistantChatMessage =>
      Boolean(
        message &&
          typeof message === 'object' &&
          typeof (message as AssistantChatMessage).id === 'string' &&
          typeof (message as AssistantChatMessage).content === 'string' &&
          ((message as AssistantChatMessage).role === 'user' ||
            (message as AssistantChatMessage).role === 'assistant'),
      ),
  )
}

export function subscribeOwnChats(
  uid: string,
  onChange: (chats: AssistantChatSummary[]) => void,
): () => void {
  const owned = query(
    collection(db, ASSISTANT_CHATS_COLLECTION),
    where('ownerId', '==', uid),
  )
  return onSnapshot(
    owned,
    snapshot => {
      const chats = snapshot.docs.flatMap(item => {
        const data = item.data()
        if (data.archivedAt) return []
        const updated = data.updatedAt as Timestamp | undefined
        const created = data.createdAt as Timestamp | undefined
        if (typeof data.assistantId === 'string' && data.assistantId) return []
        const title = typeof data.title === 'string' ? data.title.trim() : ''
        const updatedAt =
          typeof updated?.toMillis === 'function'
            ? updated.toMillis()
            : typeof created?.toMillis === 'function'
              ? created.toMillis()
              : null
        return [
          {
            id: item.id,
            title: title || 'New chat',
            updatedAt,
          },
        ]
      })
      onChange(sortChatsByRecent(chats))
    },
    () => onChange([]),
  )
}

export async function createOwnChat(uid: string, title: string): Promise<string> {
  const ref = doc(collection(db, ASSISTANT_CHATS_COLLECTION))
  await setDoc(ref, {
    ownerId: uid,
    title: chatTitleFromText(title),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export function subscribeOwnTranscript(
  chatId: string,
  onChange: (messages: AssistantChatMessage[]) => void,
  onError?: () => void,
): () => void {
  return onSnapshot(
    doc(db, ASSISTANT_CHATS_COLLECTION, chatId, 'transcript', 'current'),
    snap => {
      if (!snap.exists()) {
        onChange([])
        return
      }
      onChange(asMessages(snap.data().messages))
    },
    () => onError?.(),
  )
}

export function subscribeChatMeta(
  chatId: string,
  onChange: (meta: AssistantChatMeta | null) => void,
): () => void {
  return onSnapshot(
    doc(db, ASSISTANT_CHATS_COLLECTION, chatId),
    snap => {
      if (!snap.exists()) {
        onChange(null)
        return
      }
      const data = snap.data()
      const ownerId = typeof data.ownerId === 'string' ? data.ownerId : ''
      const assistantId =
        typeof data.assistantId === 'string' && data.assistantId ? data.assistantId : null
      onChange(ownerId ? { ownerId, assistantId } : null)
    },
    () => onChange(null),
  )
}

export async function saveOwnTranscript(
  ownerId: string,
  chatId: string,
  messages: AssistantChatMessage[],
): Promise<void> {
  const transcriptRef = doc(db, ASSISTANT_CHATS_COLLECTION, chatId, 'transcript', 'current')
  const chatRef = doc(db, ASSISTANT_CHATS_COLLECTION, chatId)
  await runTransaction(db, async transaction => {
    const snap = await transaction.get(transcriptRef)
    const current = snap.exists() ? asMessages(snap.data()?.messages) : []
    const payload = JSON.parse(
      JSON.stringify(mergeTranscriptMessages(current, messages)),
    ) as AssistantChatMessage[]
    transaction.set(transcriptRef, {
      ownerId,
      messages: payload,
      updatedAt: serverTimestamp(),
    })
    transaction.update(chatRef, { updatedAt: serverTimestamp() })
  })
}

/** Owner-only title change. Empty input is ignored. */
export async function renameOwnChat(chatId: string, title: string): Promise<void> {
  const next = normalizeAssistantChatTitle(title)
  if (!next) return
  await updateDoc(doc(db, ASSISTANT_CHATS_COLLECTION, chatId), {
    title: next,
    updatedAt: serverTimestamp(),
  })
}

export async function deleteOwnChats(chatIds: string[]): Promise<void> {
  await Promise.all(
    chatIds.map(async chatId => {
      await deleteDoc(doc(db, ASSISTANT_CHATS_COLLECTION, chatId, 'transcript', 'current')).catch(
        () => undefined,
      )
      await deleteDoc(doc(db, ASSISTANT_CHATS_COLLECTION, chatId))
    }),
  )
}
